import React from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import s from '../styles.module.scss'

import { LoginModalTypeEnum } from '../login.enum'
import { ResetPasswordInterface } from '../login.types'
import LoginModalContainer from '../login-modal-container/login-modal-container'
import { Button } from '../../../../components/atoms'
import { InputText } from '../../../../components/molecules'
import {
  breakpoints,
  useDomainFromRouter,
  useMediaQuery,
} from '../../../../lib/hooks'

export interface InputsResetPassword {
  password: string
  passwordCheck?: string
}

const ResetPasswordForm = ({
  onResetPassword,
  isLoading,
  setModalType,
}: ResetPasswordInterface) => {
  const intl = useIntl()
  const isMobile = useMediaQuery(breakpoints.newMobile)
  const domain = useDomainFromRouter()

  const {
    control,
    handleSubmit,
    formState: { isValid },
    watch,
    setError,
  } = useForm<InputsResetPassword>({
    defaultValues: { password: '', passwordCheck: '' },
    mode: 'onChange',
  })

  const onSubmit: SubmitHandler<InputsResetPassword> = async ({ password }) => {
    try {
      await onResetPassword({ password })
    } catch {
      setError('password', {
        type: 'string',
        message: intl.formatMessage({
          id: 'ui.ResetPasswordForm.submit.password',
          defaultMessage:
            'Une erreur est survenue. Veuillez réessayer ultérieurement.',
        }),
      })
      setError('passwordCheck', {
        type: 'string',
        message: intl.formatMessage({
          id: 'ui.ResetPasswordForm.submit.passwordCheck',
          defaultMessage:
            'Une erreur est survenue. Veuillez réessayer ultérieurement.',
        }),
      })
    }
  }

  return (
    <LoginModalContainer
      closeModal={() => setModalType({ type: LoginModalTypeEnum.Close })}
      title={intl.formatMessage({
        id: 'ui.ResetPasswordForm.titleModal',
        defaultMessage: 'Réinitialisez votre mot de passe',
      })}
      browserFooter={{
        label: domain ? (
          <FormattedMessage
            id='ui.forgotPassword.label.returnToLoginPage'
            defaultMessage='Retour à la page de connexion'
          />
        ) : (
          <FormattedMessage
            id='ui.ResetPasswordForm.labelBrowserFooter'
            defaultMessage="Retour à l'accueil"
          />
        ),
        onClick: () => setModalType({ type: LoginModalTypeEnum.Close }),
      }}
      mobileFooter={{
        subButton: {
          label: domain
            ? intl.formatMessage({
                id: 'ui.forgotPassword.label.returnToLoginPage',
                defaultMessage: 'Retour à la page de connexion',
              })
            : intl.formatMessage({
                id: 'ui.ResetPasswordForm.labelMobileFooter',
                defaultMessage: "Retour à la page d'accueil",
              }),
          onClick: () => setModalType({ type: LoginModalTypeEnum.Close }),
        },
        button: {
          label: intl.formatMessage({
            id: 'ui.ResetPasswordForm.labelButton',
            defaultMessage: 'Confirmer',
          }),
          onClick: handleSubmit(onSubmit),
          disabled: !isValid,
          isLoading,
        },
      }}
    >
      <form className={s.form}>
        <InputText
          control={control}
          name='password'
          label={
            <FormattedMessage
              id='ui.ResetPasswordForm.password'
              defaultMessage='Mot de passe'
            />
          }
          rules={{
            validate: (password: string | undefined) =>
              password && password.length >= 6
                ? true
                : intl.formatMessage({
                    id: 'ui.ResetPasswordForm.passwordValidate',
                    defaultMessage: 'Minimum 6 caractères',
                  }),
          }}
          inputProps={{
            type: 'password',
            'aria-label': intl.formatMessage({
              id: 'ui.ResetPasswordForm.passwordPlaceholder',
              defaultMessage: 'Entrez votre mot de passe',
            }),
            'aria-required': 'true',
          }}
          placeholder={intl.formatMessage({
            id: 'ui.ResetPasswordForm.passwordPlaceholder',
            defaultMessage: 'Entrez votre mot de passe',
          })}
        />
        <InputText
          control={control}
          name='passwordCheck'
          label={
            <FormattedMessage
              id='ui.ResetPasswordForm.confirmPassword'
              defaultMessage='Confirmez le mot de passe'
            />
          }
          rules={{
            validate: (password: string | undefined) =>
              password === watch('password')
                ? true
                : intl.formatMessage({
                    id: 'ui.ResetPasswordForm.passwordDontMatch',
                    defaultMessage: 'Les mots de passe ne correspondent pas',
                  }),
          }}
          inputProps={{
            type: 'password',
            'aria-label': intl.formatMessage({
              id: 'ui.ResetPasswordForm.confirmPasswordPlaceholder',
              defaultMessage: 'Entrez votre mot de passe',
            }),
            'aria-required': 'true',
          }}
          placeholder={intl.formatMessage({
            id: 'ui.ResetPasswordForm.confirmPasswordPlaceholder',
            defaultMessage: 'Entrez votre mot de passe',
          })}
        />

        {!isMobile && (
          <Button
            className={s.loginBtn}
            loading={isLoading}
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid}
            fullWidth={true}
          >
            <FormattedMessage
              id='ui.ResetPasswordForm.resetPassword'
              defaultMessage='Réinitialiser'
            />
          </Button>
        )}
      </form>
    </LoginModalContainer>
  )
}

export default ResetPasswordForm
